<template>
    <div>
        <b-form name="advancedStyleForm">
            <div>
                <div class="col-xs-10 col-md-10 col-lg-10 pl-0">
                    <!-- Classification type radio buttons -->
                    <b-form-group>
                        <b-form-radio-group
                            :id="`styleclassification-radio-group_${localThemeStyle.id}`"
                            v-model="
                                localThemeStyle.customStylingOptions
                                    .classificationType
                            "
                            :name="`styleclassification-radio-group_${localThemeStyle.id}`"
                            @change="$_onStyleTypeChanged"
                        >
                            <b-form-radio
                                :value="STYLE_CLASSIFICATION_TYPES.NOMINAL"
                                >{{
                                    $t('dashboard.styling.nominal')
                                }}</b-form-radio
                            >
                            <b-form-radio
                                :value="STYLE_CLASSIFICATION_TYPES.RANGES"
                                >{{
                                    $t('dashboard.styling.ranges')
                                }}</b-form-radio
                            >
                        </b-form-radio-group>
                    </b-form-group>
                    <!-- SLD Dropdown attribute -->
                    <label for="fieldInput">
                        {{ fieldInputLabel }}
                        <template v-if="isFeedback || !isNominalClassification">
                            <info-button
                                :id="`${localThemeStyle.id}_btn-info`"
                                :name="`${localThemeStyle.id}_btn-info`"
                            />
                            <tooltip
                                :target="`${localThemeStyle.id}_btn-info`"
                                :title="fieldInputTooltip"
                                :customClass="'medium'"
                            >
                            </tooltip>
                        </template>
                    </label>
                    <x-select
                        :id="`${localThemeStyle.id}_fieldInput`"
                        :name="`${localThemeStyle.id}_fieldInput`"
                        v-model="localThemeStyle.customStylingOptions.attribute"
                        @selected="$_fieldSelected"
                        :options="fieldInputOptions"
                        :state="localThemeStyle.hasAdvancedStyleError !== true"
                        :invalidMessage="$t('requiredField')"
                        :simpleMode="true"
                    >
                    </x-select>
                    <!-- Domain values for theme layers -->
                    <template v-if="showDomain">
                        <label>{{ $t('labelDomain') }}</label>
                        <x-select
                            :id="`${localThemeStyle.id}_domainInput`"
                            :name="`${localThemeStyle.id}_domainInput`"
                            v-model="
                                localThemeStyle.customStylingOptions.domainId
                            "
                            @selected="$_domainSelected"
                            :options="domainOptions"
                            :state="
                                localThemeStyle.hasAdvancedStyleErrorDomain !==
                                true
                            "
                            :invalidMessage="$t('requiredField')"
                            :simpleMode="true"
                            class="domain-select"
                        >
                        </x-select>
                        <b-link
                            :id="`${localThemeStyle.id}_addDomain`"
                            :name="`${localThemeStyle.id}_addDomain`"
                            @click="$_openDomainModal()"
                            class="domain-add-link"
                        >
                            <em class="fa fa-plus"></em>
                            {{ $t('domainAddButton') }}
                        </b-link>
                    </template>
                    <!-- Other rule -->
                    <b-form-group>
                        <x-checkbox
                            :id="`${localThemeStyle.id}_otherOption`"
                            :name="`${localThemeStyle.id}_otherOption`"
                            v-model="
                                localThemeStyle.customStylingOptions
                                    .useOtherRule
                            "
                            :text="$t('showOtherAdvancedValues')"
                        >
                        </x-checkbox>
                    </b-form-group>
                    <!-- Start/end color -->
                    <div class="form-inline pb-3">
                        <b-row class="ml-0">
                            <label :for="`${localThemeStyle.id}_colorStart`">{{
                                $t('dashboard.styling.colorStart')
                            }}</label>
                            <b-col>
                                <color-picker
                                    :id="`${localThemeStyle.id}_colorStart`"
                                    :name="`${localThemeStyle.id}_colorStart`"
                                    v-model="
                                        localThemeStyle.customStylingOptions
                                            .colorStart
                                    "
                                ></color-picker>
                            </b-col>
                            <label :for="`${localThemeStyle.id}colorEnd`">{{
                                $t('dashboard.styling.colorEnd')
                            }}</label>
                            <b-col>
                                <color-picker
                                    :id="`${localThemeStyle.id}_colorEnd`"
                                    :name="`${localThemeStyle.id}_colorEnd`"
                                    v-model="
                                        localThemeStyle.customStylingOptions
                                            .colorEnd
                                    "
                                ></color-picker>
                            </b-col>
                        </b-row>
                    </div>
                    <!-- Generate button -->
                    <b-button
                        v-if="isNominalClassification"
                        class="mb-3"
                        @click="$_generateStyleRules()"
                        variant="secondary"
                        :disabled="
                            !localThemeStyle.customStylingOptions.domainId
                        "
                    >
                        {{ $t('dashboard.styling.generate') }}
                        <span class="fas fa-check"></span>
                    </b-button>
                    <!-- SLD Rules -->
                    <!-- Nominal -->
                    <v-draggable
                        v-if="isNominalClassification"
                        :list="localThemeStyle.customStylingOptions.rules"
                        handle=".fa-arrows-alt"
                    >
                        <div
                            role="tablist"
                            v-for="(rule, index) in localThemeStyle
                                .customStylingOptions.rules"
                            :key="index"
                        >
                            <b-card v-if="showRule(rule)" no-body class="mb-1">
                                <b-card-header
                                    v-b-toggle="
                                        `accSldRules${$_getCardId(rule.title)}`
                                    "
                                    header-tag="header"
                                    class="p-1"
                                    role="tab"
                                    data-toggle="collapse"
                                >
                                    <em class="fa fa-arrows-alt"></em>
                                    <span>{{ rule.title }}</span>
                                    <span class="float-right">
                                        <!-- Delete icon in header -->
                                        <a
                                            v-if="!isOtherRule(rule)"
                                            :id="`btnDeleteRule${index}`"
                                            :name="`btnDeleteRule${index}`"
                                            :title="
                                                $t('removeAdvancedStylingRule')
                                            "
                                            class="pr-4"
                                            @click.stop.prevent="
                                                $_removeRule(index)
                                            "
                                        >
                                            <em
                                                class="fas fa-trash-alt fa-sm"
                                            ></em>
                                        </a>
                                        <!-- Expand/collapse icon -->
                                        <em class="fa iconCollapseExpand"></em>
                                    </span>
                                </b-card-header>
                                <b-collapse
                                    :id="`accSldRules${$_getCardId(
                                        rule.title
                                    )}`"
                                    accordion="sldruleaccordion"
                                    role="tabpanel"
                                    class="pl-3"
                                >
                                    <!-- Warning -->
                                    <div
                                        v-if="
                                            isOtherRule(rule) &&
                                            geometryTypeArray.length > 1
                                        "
                                        class="error pl-2 pr-2 pt-2"
                                    >
                                        <em
                                            class="error fas fa-exclamation-triangle"
                                        />
                                        {{ otherRuleWarning }}
                                    </div>
                                    <!-- Title -->
                                    <div class="form-inline style-item pt-3">
                                        <div class="form-group">
                                            <label
                                                for="`title_${rule.title}_${localThemeStyle.id}`"
                                                class="custom style-label-md sld-attribute"
                                            >
                                                {{ $t('columnNameTitle') }}
                                            </label>
                                            <x-input
                                                :name="`title_${rule.title}_${localThemeStyle.id}`"
                                                :id="`title_${rule.title}_${localThemeStyle.id}`"
                                                v-model="rule.title"
                                                required
                                            >
                                            </x-input>
                                        </div>
                                    </div>
                                    <!-- Filter literal -->
                                    <div class="form-inline style-item">
                                        <div class="form-group">
                                            <label
                                                for="`literal_${rule.title}_${localThemeStyle.id}`"
                                                class="custom style-label-md sld-attribute"
                                            >
                                                {{
                                                    $t('labelStyleDomainValue')
                                                }}
                                            </label>
                                            <x-input
                                                :name="`literal_${rule.title}_${localThemeStyle.id}`"
                                                :id="`literal_${rule.title}_${localThemeStyle.id}`"
                                                v-model="rule.filterLiteral"
                                                :plaintext="true"
                                                required
                                            >
                                            </x-input>
                                        </div>
                                    </div>
                                    <!-- Line Attribute component -->
                                    <line-attribute
                                        v-if="$_showLineStyle(index)"
                                        :settings="$_getSettings(index)"
                                        :name="`lineStyle${rule.title}_${localThemeStyle.id}`"
                                        :geometryTypeArray="geometryTypeArray"
                                        :isIconSelected="
                                            $_isIconSelected(index)
                                        "
                                        @update="
                                            $_updateSettings($event, index)
                                        "
                                        :showSvgStyler="true"
                                    >
                                    </line-attribute>
                                    <!-- Icon -->
                                    <div
                                        v-if="hasPointGeometry"
                                        class="form-inline style-item"
                                    >
                                        <div
                                            class="form-group align-items-start"
                                        >
                                            <label
                                                class="custom style-label-md"
                                            >
                                                {{ $t('icon') }}
                                            </label>
                                            <image-loader
                                                :id="`icon_${rule.title}_${localThemeStyle.id}`"
                                                :name="`icon_${rule.title}_${localThemeStyle.id}`"
                                                :imgExtendedClass="
                                                    imgExtendedClass(
                                                        index,
                                                        rule
                                                    )
                                                "
                                                v-model="rule.icon"
                                            />
                                        </div>
                                    </div>
                                    <!-- Svg styler for icon -->
                                    <div
                                        v-if="isSvgIcon(index, rule)"
                                        class="form-inline"
                                    >
                                        <label class="style-label-md"></label>
                                        <svg-styler
                                            :source="rule.icon"
                                            :hasPointGeometry="true"
                                            :settings="getIconSettings(index)"
                                            @update="
                                                $_updateIconSettings(
                                                    $event,
                                                    index
                                                )
                                            "
                                        >
                                        </svg-styler>
                                    </div>
                                </b-collapse>
                            </b-card>
                        </div>
                    </v-draggable>
                    <!-- Ranges -->
                    <ranges
                        v-if="!isNominalClassification"
                        :customStylingOptions="
                            localThemeStyle.customStylingOptions
                        "
                        @update="$_updateCustomStylingOptions($event)"
                        :layerSourceName="layerSourceName"
                        :attribute="
                            localThemeStyle.customStylingOptions.attribute
                        "
                        :hasPointGeometryOnly="hasPointGeometryOnly"
                        :hasPointGeometry="hasPointGeometry"
                        :geometryTypeArray="geometryTypeArray"
                    >
                    </ranges>
                </div>
            </div>
        </b-form>
        <add-domain-modal
            :launchModal="launchDomainModal"
            @modal-generated-domain="$_onModalGeneratedDomain"
            @modal-domain-saved="$_onModalDomainSaved"
        >
        </add-domain-modal>
    </div>
</template>

<script>
    import {
        CUSTOM_STYLING_RULE_OTHER,
        FEEDBACKS_GEOMETRY_TYPE_CODES,
        STYLE_CLASSIFICATION_TYPES,
        NUMERIC_DATATYPES,
        FIELD_TYPE
    } from '../../../constants';
    import {
        arraySortAscendingFunction,
        convertToValueTextPair,
        isSvgBase64Image
    } from '../../../services/utilities';
    import { Gradient } from '../../../services/dashboard/gradient';
    import { orderBy } from 'lodash';
    import draggable from 'vuedraggable';
    import { ICON_UNITS } from '../../../constants/api';
    import XInput from '../../../components/BaseComponents/x-input.vue';
    import XSelect from '../../../components/BaseComponents/x-select.vue';
    import XCheckbox from '../../../components/BaseComponents/x-checkbox.vue';
    import modalMixin from '../../../components/Mixins/ModalMixin.vue';
    import lineAttribute from './LineAttribute.vue';
    import InfoButton from '../../../components/InfoButton.vue';
    import Tooltip from '../../../components/Tooltip.vue';
    import AddDomainModal from '../../../components/AddDomainModal.vue';
    import imageLoader from '../../../components/ImageLoader.vue';
    import SvgStyler from '../../../components/BaseComponents/SvgStyler.vue';
    import ColorPicker from '../../../components/ColorPicker.vue';
    import Ranges from '../../../components/dashboard/StylingOptions/Ranges.vue';

    export default {
        name: 'StyledLayerDescriptorDetailForm',

        mixins: [modalMixin],

        components: {
            'x-input': XInput,
            'x-select': XSelect,
            'x-checkbox': XCheckbox,
            'v-draggable': draggable,
            'line-attribute': lineAttribute,
            'info-button': InfoButton,
            tooltip: Tooltip,
            'add-domain-modal': AddDomainModal,
            'image-loader': imageLoader,
            'svg-styler': SvgStyler,
            'color-picker': ColorPicker,
            ranges: Ranges
        },

        props: {
            themeStyle: { type: Object, required: true },
            inputFields: { type: Array, required: false, default: () => [] },
            domains: { type: Array, required: false, default: () => [] },
            isFeedback: { type: Boolean, required: false, default: false },
            geometryTypeArray: { type: Array, required: false, default: () => [] },
            layerSourceName: {
                type: String,
                required: false,
                default: ''
            }
        },

        data() {
            return {
                launchDomainModal: false,
                domainsExtended: this.domains,
                // we create a local variable for themestyle: then eslint dont complains
                // about "Unexpected mutation of "themeStyle" prop vue/no-mutating-props"
                localThemeStyle: this.themeStyle,
                STYLE_CLASSIFICATION_TYPES: STYLE_CLASSIFICATION_TYPES
            };
        },

        created() {
            this.$_addOtherRule();
            this.$_addMissingProperties();
        },

        computed: {
            fieldInputLabel() {
                return this.isFeedback
                    ? this.$t('labelFeedbackField')
                    : this.$t('labelAttributeName');
            },
            fieldInputTooltip() {
                return this.isNominalClassification
                    ? this.$t('customStylingFeedbackFieldDomain')
                    : this.$t('customStylingFeedbackFieldNumeric');
            },
            domainOptions() {
                if (this.domainsExtended.length > 0) {
                    const orderedDomains = orderBy(this.domainsExtended, [
                        (domain) => domain.name.toLowerCase()
                    ]);
                    return convertToValueTextPair(orderedDomains, 'id', 'name');
                }
                return [];
            },
            fieldInputOptions() {
                let attributes = this.inputFields.map((item) => {
                    return {
                        ...item,
                        alias: item.alias ? item.alias : item.attribute
                    };
                });

                let sortKey = 'attribute';
                if (this.isFeedback) {
                    sortKey = 'name';
                    if (!this.isNominalClassification) {
                        attributes = attributes.filter(
                            ({ type }) =>
                                type === FIELD_TYPE.NUMERIC ||
                                type === FIELD_TYPE.NUMERICWITHDECIMALS
                        );
                    } else {
                        attributes = attributes.filter(({ domain }) => domain);
                    }
                } else if (!this.isNominalClassification) {
                    attributes = attributes.filter(({ dataType }) =>
                        NUMERIC_DATATYPES.includes(dataType.replace('xsd:', ''))
                    );
                }

                const sortedOptions = orderBy(attributes, [
                    (attribute) => attribute.alias.toLowerCase()
                ]);

                return convertToValueTextPair(sortedOptions, sortKey, 'alias');
            },
            isNominalClassification() {
                return (
                    this.localThemeStyle.customStylingOptions.classificationType ===
                    STYLE_CLASSIFICATION_TYPES.NOMINAL
                );
            },
            showDomain() {
                return (
                    !this.isFeedback &&
                    this.localThemeStyle.customStylingOptions.attribute !== null &&
                    this.isNominalClassification
                );
            },
            otherRuleWarning() {
                const geometry = this.geometryTypeArray
                    .join('/')
                    .replace('P', this.$t('geomPoint'))
                    .replace('L', this.$t('geomLine'))
                    .replace('V', this.$t('geomPolygon'));
                return this.$t('warningRuleOther', [geometry]);
            },
            hasPointGeometry() {
                return this.geometryTypeArray.includes('P');
            },
            hasPointGeometryOnly() {
                return (
                    this.geometryTypeArray.length > 0 &&
                    this.geometryTypeArray.every((item) =>
                        [FEEDBACKS_GEOMETRY_TYPE_CODES.POINT].includes(item)
                    )
                );
            }
        },

        methods: {
            $_getSettings(index) {
                return {
                    fillColor:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .fillColor || 'rgba(0,0,0,1)',
                    strokeColor:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .strokeColor || 'rgba(0,0,0,1)',
                    strokeWidth:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .strokeWidth || 3,
                    pointSize:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .pointSize || 5,
                    nameSvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .nameSvg || null,
                    fillSvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .fillSvg || '#ffffff',
                    fillOpacitySvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .fillOpacitySvg || 1,
                    strokeSvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .strokeSvg || '#000000',
                    strokeWidthSvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .strokeWidthSvg || 1,
                    strokeOpacitySvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .strokeOpacitySvg || 1,
                    sizeSvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .sizeSvg || 15,
                    rotationSvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .rotationSvg || 0
                };
            },
            getIconSettings(index) {
                return {
                    fillSvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .iconFill || '#ffffff',
                    fillOpacitySvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .iconFillOpacity || 1,
                    strokeSvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .iconStroke || '#000000',
                    strokeWidthSvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .iconStrokeWidth || 1,
                    strokeOpacitySvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .iconStrokeOpacity || 1,
                    sizeSvg:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .iconSize || 15,
                    sizeUnits:
                        this.localThemeStyle.customStylingOptions.rules[`${index}`]
                            .iconSizeUnits || ICON_UNITS.pixels
                };
            },
            $_addOtherRule() {
                // checks if 'other' rule is found in already existing rules, if not: add it
                if (this.localThemeStyle.customStylingOptions.rules.length > 0) {
                    const otherRuleFound =
                        this.localThemeStyle.customStylingOptions.rules.find(
                            (rule) =>
                                rule.filterLiteral === CUSTOM_STYLING_RULE_OTHER
                        );
                    if (!otherRuleFound) {
                        const otherRule = this.$_getEmptyRule({
                            key: CUSTOM_STYLING_RULE_OTHER,
                            value: this.$t('labelOther')
                        });
                        this.localThemeStyle.customStylingOptions.rules.push(
                            otherRule
                        );
                    }
                }
            },
            $_addMissingProperties() {
                if (!this.localThemeStyle.customStylingOptions.classificationType) {
                    this.localThemeStyle.customStylingOptions.classificationType =
                        STYLE_CLASSIFICATION_TYPES.NOMINAL;
                }
                if (!this.localThemeStyle.customStylingOptions.colorStart) {
                    this.localThemeStyle.customStylingOptions.colorStart =
                        'rgba(255, 0, 0, 1)';
                }
                if (!this.localThemeStyle.customStylingOptions.colorEnd) {
                    this.localThemeStyle.customStylingOptions.colorEnd =
                        'rgba(0, 0, 255, 1)';
                }
            },
            $_clearSldRules() {
                this.localThemeStyle.customStylingOptions.rules = [];
            },
            async $_createStyleRules(createNewRules) {
                // Get details for selected domain from DB
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetFeedbackDomain',
                    parameters: {
                        id: this.localThemeStyle.customStylingOptions.domainId
                    }
                });
                let keyvalues = null;
                if (result?.data) {
                    keyvalues = result.data.keyvalues;
                }

                if (
                    !this.localThemeStyle.customStylingOptions.attribute ||
                    !keyvalues
                ) {
                    return;
                }
                let otherRule = null;
                let ruleArray = [];

                // Create new rules bases on current content of domain.
                if (createNewRules) {
                    // Overwrite existing array
                    keyvalues.forEach((keyvalue) => {
                        const rule = this.$_getEmptyRule(keyvalue);
                        ruleArray.push(rule);
                    });
                    ruleArray.sort(arraySortAscendingFunction('title'));
                } else {
                    // Use existing array, dont change order of items
                    // dont change item 'overig' too
                    // so save it for reuse later
                    // Only select style values that are in the domain
                    const matchingRules =
                        this.localThemeStyle.customStylingOptions.rules.filter(
                            (rule) =>
                                rule.filterLiteral === CUSTOM_STYLING_RULE_OTHER || // Always include the "Overig" rule
                                keyvalues.some(
                                    (kv) => kv.key === rule.filterLiteral
                                ) // Check if the rule matches any key in domain
                        );

                    // Add matching rules
                    matchingRules.forEach((keyvalue) => {
                        if (keyvalue.filterLiteral === CUSTOM_STYLING_RULE_OTHER) {
                            // save for add again later
                            otherRule = JSON.parse(JSON.stringify(keyvalue));
                        } else {
                            ruleArray.push(keyvalue);
                        }
                    });

                    // Get all domainValues not in the style.
                    const filteredKeyValues = keyvalues.filter(
                        (kv) =>
                            !matchingRules.some(
                                (rule) => rule.filterLiteral === kv.key
                            )
                    );

                    // Add default rule for missing domain values
                    if (filteredKeyValues.length > 0) {
                        // Add a default style for the missing domain values
                        filteredKeyValues.forEach((keyvalue) => {
                            const rule = this.$_getEmptyRule(keyvalue);
                            ruleArray.push(rule);
                        });
                    }
                }
                this.localThemeStyle.customStylingOptions.rules = null;
                this.$nextTick(() => {
                    // Generate color gradients
                    ruleArray = this.$_generateGradients(
                        ruleArray.length,
                        ruleArray
                    );
                    if (createNewRules) {
                        // Add 'other' rule at the end
                        otherRule = this.$_getEmptyRule({
                            key: CUSTOM_STYLING_RULE_OTHER,
                            value: this.$t('labelOther')
                        });
                        otherRule.fillColor = 'rgba(255,255,0,1)'; // yellow
                    }
                    // add created or saved rule for 'Overig'
                    ruleArray.push(otherRule);
                    this.localThemeStyle.customStylingOptions.rules = ruleArray;
                });
            },
            $_generateGradients(nrColors, ruleArray) {
                const colorArray = new Gradient()
                    .setColorGradient(
                        this.localThemeStyle.customStylingOptions.colorStart,
                        this.localThemeStyle.customStylingOptions.colorEnd
                    )
                    .setNumberOfColors(nrColors)
                    .getColors();
                return ruleArray.map((rule, index) => {
                    return {
                        ...rule,
                        fillColor: colorArray[index]
                    };
                });
            },
            async $_generateSldRules() {
                // Skip if attribute or domain not set
                if (
                    !this.localThemeStyle.customStylingOptions.attribute ||
                    !this.localThemeStyle.customStylingOptions.domainId
                ) {
                    return;
                }

                // Clear existing rules
                this.$_clearSldRules();

                // Get details for selected domain from DB
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetFeedbackDomain',
                    parameters: {
                        id: this.localThemeStyle.customStylingOptions.domainId
                    }
                });
                if (result?.data) {
                    this.$_createStyleRules(true);
                }
            },
            $_getEmptyRule(keyvalue) {
                return {
                    // Default rule values
                    title: keyvalue.value,
                    filterPropertyName:
                        this.localThemeStyle.customStylingOptions.attribute,
                    filterLiteral: keyvalue.key,
                    fillColor: 'rgba(0,0,0,1)',
                    strokeColor: 'rgba(0,0,0,1)',
                    strokeWidth: 3,
                    pointSize: 5,
                    icon: null,
                    nameSvg: null,
                    fillSvg: '#ffffff',
                    fillOpacitySvg: 1,
                    strokeSvg: '#000000',
                    strokeOpacitySvg: 1,
                    strokeWidthSvg: 1,
                    sizeSvg: 15,
                    sizeUnits: ICON_UNITS.pixels,
                    rotationSvg: 0
                };
            },
            $_removeRule(index) {
                const rule = this.localThemeStyle.customStylingOptions.rules[index];
                const data = {
                    message: this.$t('deleteConfirmation', [`${rule.title}`])
                };
                this.msgBoxConfirm(data).then((confirmed) => {
                    if (confirmed) {
                        this.localThemeStyle.customStylingOptions.rules.splice(
                            index,
                            1
                        );
                    }
                });
            },
            /**
             * Returns the title without spaces
             * @param title of the rule
             */
            $_getCardId(title) {
                return title?.replace(/\s+/g, '');
            },
            /**
             * Inputfield selected event handler
             * @param {object} input event value
             */
            $_fieldSelected(input) {
                if (input === null) {
                    this.localThemeStyle.customStylingOptions.attribute = null;
                    this.localThemeStyle.customStylingOptions.rules = [];
                    this.localThemeStyle.customStylingOptions.domainId = null;
                    this.localThemeStyle.customStylingOptions.useOther = false;
                    return;
                }

                this.localThemeStyle.customStylingOptions.attribute = input;

                if (this.isFeedback) {
                    // Find the domainId for the selected field name
                    const domainId = this.inputFields.find(
                        (f) => f.name === input
                    ).domainId;
                    this.$_setDomainId(domainId);
                    this.$_generateSldRules();
                }
                if (this.localThemeStyle.hasAdvancedStyleError) {
                    this.localThemeStyle.hasAdvancedStyleError = !input;
                    this.$emit('reevaluatestyle');
                }
            },
            /**
             * Domain selected event handler
             * @param {object} domainId event value
             */
            $_domainSelected(domainId) {
                if (domainId === null) {
                    this.localThemeStyle.customStylingOptions.rules = [];
                    this.localThemeStyle.customStylingOptions.domainId = null;
                    return;
                }
                if (
                    this.localThemeStyle.customStylingOptions.domainId ===
                        domainId &&
                    this.localThemeStyle.customStylingOptions.rules.length > 1
                ) {
                    // Generate new color gradients
                    this.$_createStyleRules(false);
                    return;
                }
                this.$_setDomainId(domainId);
                this.$_generateSldRules();
                if (this.localThemeStyle.hasAdvancedStyleErrorDomain) {
                    this.localThemeStyle.hasAdvancedStyleErrorDomain = !domainId;
                    this.$emit('reevaluatestyle');
                }
            },
            $_setDomainId(domainId) {
                this.localThemeStyle.customStylingOptions.domainId = domainId;
            },
            $_openDomainModal() {
                // The prop value of Modal needs to be changed in order to trigger
                // the Watch function in AddDomainModal component
                this.launchDomainModal = !this.launchDomainModal;
            },
            async $_getAllDomains() {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetFeedbackDomainOverview'
                });
                if (result?.data) {
                    this.domainsExtended = [...result.data];
                }
            },
            /**
             * Sets the new generated domain as selected
             * @param data
             */
            $_onModalGeneratedDomain(data) {
                this.domainsExtended.push({
                    id: data.domain.id,
                    name: data.domain.name
                });
                this.$_setDomainId(data.domain.id);
            },
            /**
             * Sets the new domain as selected
             * @param data
             */
            $_onModalDomainSaved(data) {
                this.$_getAllDomains();
                const message = this.getDomainModalMessage(data);
                if (message) {
                    this.showToast({
                        message: message,
                        variant: 'danger',
                        autoHideDelay: 5000,
                        noCloseButton: true
                    });
                }
                this.$_setDomainId(data.id);
            },
            isOtherRule(rule) {
                return rule.filterLiteral === CUSTOM_STYLING_RULE_OTHER;
            },
            showRule(rule) {
                return this.isOtherRule(rule)
                    ? this.localThemeStyle.customStylingOptions.useOtherRule
                    : true;
            },
            $_isIconSelected(index) {
                return !!this.localThemeStyle.customStylingOptions.rules[`${index}`]
                    .icon;
            },
            isSvgIcon(index, rule) {
                return this.$_isIconSelected(index) && isSvgBase64Image(rule.icon);
            },
            imgExtendedClass(index, rule) {
                return this.isSvgIcon(index, rule) ? 'd-none' : 'pb-2';
            },
            $_showLineStyle(index) {
                return !this.hasPointGeometryOnly || !this.$_isIconSelected(index);
            },
            $_updateSettings(event, index) {
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].fillColor = event.fillColor;
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].strokeColor = event.strokeColor;
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].strokeWidth = event.strokeWidth;
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].pointSize = event.pointSize;
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].nameSvg = event.nameSvg;
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].fillSvg = event.fillSvg;

                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].fillOpacitySvg = event.fillOpacitySvg;

                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].strokeSvg = event.strokeSvg;

                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].strokeWidthSvg = event.strokeWidthSvg;

                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].strokeOpacitySvg = event.strokeOpacitySvg;
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].sizeSvg = event.sizeSvg;
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].rotationSvg = event.rotationSvg;
            },
            $_updateIconSettings(event, index) {
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].iconFill = event.fillSvg;
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].iconFillOpacity = event.fillOpacitySvg;
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].iconStroke = event.strokeSvg;
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].iconStrokeWidth = event.strokeWidthSvg;
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].iconStrokeOpacity = event.strokeOpacitySvg;
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].iconSize = event.sizeSvg;
                this.localThemeStyle.customStylingOptions.rules[
                    `${index}`
                ].iconSizeUnits = event.sizeUnits;
            },
            $_onStyleTypeChanged(evt) {
                // Clear existing rules
                this.$_clearSldRules();

                // Find the index of selected item
                const selectedItemIndex = this.fieldInputOptions.findIndex(
                    ({ value }) =>
                        value ===
                        this.localThemeStyle.customStylingOptions.attribute
                );
                // Set option to 'Selecteer' if attribute cannot be found
                if (selectedItemIndex < 0) {
                    this.localThemeStyle.customStylingOptions.attribute = null;
                }
            },
            $_updateCustomStylingOptions(newVal) {
                this.localThemeStyle.customStylingOptions = newVal;
            },
            $_generateStyleRules() {
                this.$_domainSelected(
                    this.localThemeStyle.customStylingOptions.domainId
                );
            }
        }
    };
</script>

<style>
.domain-select .form-group {
    margin-bottom: 0.3rem;
}
</style>

<style scoped>
.fa-arrows-alt:hover {
    cursor: move;
}

.domain-add-link {
    display: block;
    margin-bottom: 1rem;
}
</style>
